import { useEffect, useMemo, useState } from 'react';
import { Search, Check, AlertCircle } from 'lucide-react';
import { teamMembersService, type TeamMember } from '../../../services/team-members.service';
import { UserAvatar } from '../../UserAvatar';

interface TeamAgentPickerProps {
  value: string[];
  onChange: (agentIds: string[]) => void;
  maxSize?: number;
  disabled?: boolean;
}

function displayName(m: TeamMember): string {
  if (m.displayName) return m.displayName;
  if (m.firstName || m.lastName) return `${m.firstName || ''} ${m.lastName || ''}`.trim();
  return m.email;
}

export function TeamAgentPicker({ value, onChange, maxSize, disabled }: TeamAgentPickerProps) {
  const [agents, setAgents] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await teamMembersService.getTeamMembers({
          role: 'agent',
          status: 'active',
          limit: 500,
        });
        if (!cancelled) setAgents(res.data);
      } catch (e) {
        if (!cancelled) setError('Failed to load agents');
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return agents;
    const q = search.toLowerCase();
    return agents.filter(
      (m) =>
        displayName(m).toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [agents, search]);

  const isAtCapacity = typeof maxSize === 'number' && value.length >= maxSize;

  const toggle = (id: string) => {
    if (disabled) return;
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      if (isAtCapacity) return;
      onChange([...value, id]);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading agents...</div>;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
          <div className="text-sm text-yellow-800 dark:text-yellow-200">
            No agents found. Invite users with the <strong>agent</strong> role from
            <strong> Settings → Team Members</strong> first.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents..."
            disabled={disabled}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
          />
        </div>
        <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
          {value.length}
          {typeof maxSize === 'number' ? ` / ${maxSize}` : ''} selected
        </div>
      </div>

      {isAtCapacity && (
        <div className="flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          <AlertCircle className="h-3.5 w-3.5" />
          Team is at capacity. Remove an agent to add another, or increase the team size.
        </div>
      )}

      <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No matches
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => {
              const selected = value.includes(m.id);
              const blocked = !selected && isAtCapacity;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => toggle(m.id)}
                    disabled={disabled || blocked}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      selected
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div
                      className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                        selected
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {selected && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <UserAvatar
                      src={m.avatarUrl}
                      name={displayName(m)}
                      className="h-8 w-8 flex-shrink-0 bg-green-100 text-sm font-medium text-green-700 dark:bg-green-900 dark:text-green-300"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {displayName(m)}
                      </div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {m.email}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
